/* eslint-disable import/prefer-default-export */
import { casingFormat, formatName, transformZipCode } from 'common/utils';
import { first, map, omit, toLower } from 'lodash';

export const transformListLineItems = ({ listLineItems }) => {
  return {
    count: listLineItems.count,
    lineItems: map(listLineItems.results, ({ patient, partner, lineItem }) => {
      const { firstName, lastName } = patient.patientDemographics;
      const itemName = lineItem.item.patientMedication?.name || '';

      return {
        ...omit(lineItem, 'item'),
        mpi: patient.mpi,
        patientName: `${lastName}, ${firstName}`,
        partnerName: partner.partnerName,
        itemName: itemName ? casingFormat(itemName) : itemName,
      };
    }),
  };
};

export const transformBulkLabels = ({ listBulkLabels }) => ({
  count: listBulkLabels.count,
  bulkLabels: map(listBulkLabels.results, ({ patient, ...bulkLabels }) => {
    const { firstName, lastName } = patient.patientDemographics;
    return {
      ...bulkLabels,
      mpi: patient.mpi,
      patientName: `${casingFormat(formatName(lastName))}, ${casingFormat(formatName(firstName))}`,
      ...(bulkLabels.medicationName
        ? { medicationName: casingFormat(bulkLabels.medicationName) }
        : {}),
      ...(bulkLabels.strength ? { strength: casingFormat(bulkLabels.strength) } : {}),
    };
  }),
});

export const transformListOrdersForProductFulfillment = ({ listOrdersForProductFulfillment }) => {
  return {
    count: listOrdersForProductFulfillment.count,
    orders: map(listOrdersForProductFulfillment.orders, ({ patient, partner, ...order }) => {
      const { firstName, lastName } = patient.patientDemographics;
      const { qs1PatientId } = patient.patientIdentity;

      return {
        ...order,
        lineItems: map(order.lineItems, (lineItem) => ({
          ...lineItem,
          ...(lineItem.itemName ? { itemName: casingFormat(lineItem.itemName) } : {}),
        })),
        mpi: patient.mpi,
        patientName: `${lastName}, ${firstName}`,
        qs1Id: `${qs1PatientId}`,
        partnerName: partner.partnerName,
      };
    }),
  };
};

export const transformListRxIntakeRequests = ({ listRxIntakeRequests }) => ({
  count: listRxIntakeRequests.count,
  result: map(
    listRxIntakeRequests.result,
    ({ patient, prescriptions, practice, requestPayload, ...requestDetails }) => {
      const { originalPayload, eScriptMetaData, enrichedPrescriptionPayload } = requestPayload;
      const { firstName, lastName, mpi } = patient;

      const { patient: patientInfo, prescriptions: prescriptionsRecord } =
        enrichedPrescriptionPayload;
      const prescriptionInfo = first(prescriptionsRecord);
      const { prescribedBy, transferDetails } = prescriptionInfo;

      return {
        mpi,
        patientName: `${casingFormat(formatName(lastName))}, ${casingFormat(
          formatName(firstName)
        )}`,
        practiceName: requestPayload?.eScriptMetaData?.practiceName,
        medicationName: first(prescriptions)?.description
          ? casingFormat(first(prescriptions).description)
          : first(prescriptions)?.description,
        partnerAccountName: enrichedPrescriptionPayload?.partnerAccount,
        requestPayload: {
          ...requestPayload,
          enrichedPrescriptionPayload: {
            partnerAccountId: requestDetails?.partnerAccountId,
            ...omit(enrichedPrescriptionPayload, 'prescriptions'),
            patient: {
              ...patientInfo,
              gender: toLower(patientInfo?.gender),
              patientAddress: {
                ...patientInfo?.patientAddress,
                addressZip: transformZipCode(patientInfo?.patientAddress?.addressZip),
              },
            },
            prescription: {
              ...prescriptionInfo,
              // POM-6361: the prescriber address is not used; the practice address is.
              prescribedBy: omit(prescribedBy, 'address'),
              transferDetails: {
                ...transferDetails,
                toPharmacy: {
                  ...transferDetails?.toPharmacy,
                  pharmacyAddress: {
                    ...transferDetails?.toPharmacy?.pharmacyAddress,
                    addressZip: transformZipCode(
                      transferDetails?.toPharmacy?.pharmacyAddress?.addressZip
                    ),
                  },
                },
              },
            },
          },
          documentId: eScriptMetaData?.docuTrackId,
          originalPrescriptionPayload:
            originalPayload?.payloadJson?.eRXs?.message?.body?.newRx || originalPayload,
        },
        ...requestDetails,
      };
    }
  ),
});
